import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Paper,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import vectorStoreService from '../services/VectorStoreService';

const VECTOR_STORE_VENDORS = [
    { value: 'Pinecone', label: 'Pinecone' },
    { value: 'AWS-OpenSearch', label: 'AWS-OpenSearch' },
];

const EMBEDDING_VENDORS = [
    { value: 'OpenAI', label: 'OpenAI' },
    { value: 'AWS', label: 'AWS' },
];

const EMBEDDING_MODELS_BY_VENDOR = {
    OpenAI: [
        { value: 'text-embedding-3-small', label: 'text-embedding-3-small' },
        { value: 'text-embedding-3-large', label: 'text-embedding-3-large' },
    ],
    AWS: [
        { value: 'cohere.embed-v4:0', label: 'cohere.embed-v4:0' },
    ],
};

const EMBEDDING_DIMENSIONS_BY_MODEL = {
    'text-embedding-3-small': [1536, 512],
    'text-embedding-3-large': [3072, 1024, 256],
    'cohere.embed-v4:0': [1536],
};

function normalizeString(value) {
    return String(value ?? '').trim();
}

function normalizeNumber(value) {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    return Number.isFinite(n) ? n : '';
}

function formatCreatedAt(createdAt) {
    const n = Number(createdAt);
    if (!Number.isFinite(n) || n <= 0) return '';

    // Backends sometimes return seconds; sometimes ms. Heuristic: < 10^12 => seconds.
    const ms = n < 1_000_000_000_000 ? n * 1000 : n;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString();
}

function VectorStoreDialog({ open, mode, initialVectorStore, onClose, onSubmit, submitting }) {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [indexName, setIndexName] = useState('');
    const [vendor, setVendor] = useState('');
    const [embeddingModel, setEmbeddingModel] = useState('');
    const [embeddingVendor, setEmbeddingVendor] = useState('');
    const [description, setDescription] = useState('');
    const [dimensions, setDimensions] = useState('');
    const [hasEmbeddings, setHasEmbeddings] = useState(false);

    useEffect(() => {
        if (!open) return;
        const vs = initialVectorStore || {};
        setName(normalizeString(vs.name));
        setIndexName(normalizeString(vs.indexName));
        setVendor(normalizeString(vs.vendor));
        setEmbeddingModel(normalizeString(vs.embeddingModel));
        setEmbeddingVendor(normalizeString(vs.embeddingVendor));
        setDescription(normalizeString(vs.description));
        setDimensions(vs.dimensions === 0 ? 0 : normalizeNumber(vs.dimensions));
        setHasEmbeddings(Boolean(vs.hasEmbeddings ?? vs.hasEmbeddins));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialVectorStore?.vectorStoreId]);

    useEffect(() => {
        if (!open) return;
        const current = normalizeString(vendor);
        if (current) return;
        const first = VECTOR_STORE_VENDORS[0]?.value;
        if (first) setVendor(first);
    }, [open, vendor]);

    const embeddingModelOptions = useMemo(() => {
        const v = normalizeString(embeddingVendor);
        return EMBEDDING_MODELS_BY_VENDOR[v] ?? [];
    }, [embeddingVendor]);

    const embeddingDimensionsOptions = useMemo(() => {
        const m = normalizeString(embeddingModel);
        return EMBEDDING_DIMENSIONS_BY_MODEL[m] ?? [];
    }, [embeddingModel]);

    useEffect(() => {
        if (!open) return;
        const current = normalizeString(embeddingVendor);
        if (current) return;
        const first = EMBEDDING_VENDORS[0]?.value;
        if (first) setEmbeddingVendor(first);
    }, [open, embeddingVendor]);

    useEffect(() => {
        if (!open) return;

        const model = normalizeString(embeddingModel);
        const hasModel = embeddingModelOptions.some((o) => o.value === model);
        const nextModel = embeddingModelOptions[0]?.value;
        if ((!model && nextModel) || (model && !hasModel && nextModel)) {
            setEmbeddingModel(nextModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, embeddingVendor]);

    useEffect(() => {
        if (!open) return;

        const n = normalizeNumber(dimensions);
        const allowed = embeddingDimensionsOptions;
        const nextDim = allowed[0];

        const hasAllowed = allowed.some((d) => d === n);
        const isUnset = n === '';
        if ((isUnset && nextDim !== undefined) || (!isUnset && !hasAllowed && nextDim !== undefined)) {
            setDimensions(nextDim);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, embeddingModel]);

    const title = mode === 'edit' ? t('vectorStores.editVectorStore', 'Edit Vector Store') : t('vectorStores.newVectorStore', 'New Vector Store');

    const canSubmit = Boolean(normalizeString(name));

    const handleSubmit = async () => {
        const payload = {
            name: normalizeString(name),
            indexName: normalizeString(indexName),
            vendor: normalizeString(vendor),
            embeddingModel: normalizeString(embeddingModel),
            embeddingVendor: normalizeString(embeddingVendor),
            description: normalizeString(description),
            hasEmbeddings: Boolean(hasEmbeddings),
            dimensions: (() => {
                const n = normalizeNumber(dimensions);
                return n === '' ? undefined : n;
            })(),
        };

        // Remove undefined fields so we don't overwrite values unintentionally.
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

        await onSubmit(payload);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    pt: 3,
                    overflowY: 'visible',
                }}
            >
                <TextField
                    label={t('vectorStores.name', 'Name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    fullWidth
                    disabled={submitting}
                />
                <TextField
                    label={t('vectorStores.indexName', 'Index Name')}
                    value={indexName}
                    onChange={(e) => setIndexName(e.target.value)}
                    fullWidth
                    disabled={submitting}
                />
                <TextField
                    label={t('vectorStores.vendor', 'Vendor')}
                    select
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    fullWidth
                    disabled={submitting}
                >
                    {(() => {
                        const current = normalizeString(vendor);
                        const exists = VECTOR_STORE_VENDORS.some((v) => v.value === current);
                        if (current && !exists) {
                            return <MenuItem value={current}>{t('vectorStores.currentOption', 'Current: {{value}}', { value: current })}</MenuItem>;
                        }
                        return null;
                    })()}
                    {VECTOR_STORE_VENDORS.map((v) => (
                        <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label={t('vectorStores.embeddingVendor', 'Embedding Vendor')}
                    select
                    value={embeddingVendor}
                    onChange={(e) => setEmbeddingVendor(e.target.value)}
                    fullWidth
                    disabled={submitting}
                >
                    {(() => {
                        const current = normalizeString(embeddingVendor);
                        const exists = EMBEDDING_VENDORS.some((v) => v.value === current);
                        if (current && !exists) {
                            return <MenuItem value={current}>{t('vectorStores.currentOption', 'Current: {{value}}', { value: current })}</MenuItem>;
                        }
                        return null;
                    })()}
                    {EMBEDDING_VENDORS.map((v) => (
                        <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label={t('vectorStores.embeddingModel', 'Embedding Model')}
                    select
                    value={embeddingModel}
                    onChange={(e) => setEmbeddingModel(e.target.value)}
                    fullWidth
                    disabled={submitting}
                >
                    {(() => {
                        const current = normalizeString(embeddingModel);
                        const exists = embeddingModelOptions.some((m) => m.value === current);
                        if (current && !exists) {
                            return <MenuItem value={current}>{t('vectorStores.currentOption', 'Current: {{value}}', { value: current })}</MenuItem>;
                        }
                        return null;
                    })()}
                    {embeddingModelOptions.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label={t('vectorStores.dimensions', 'Dimensions')}
                    select
                    value={dimensions}
                    onChange={(e) => setDimensions(e.target.value)}
                    fullWidth
                    disabled={submitting}
                >
                    {(() => {
                        const current = normalizeNumber(dimensions);
                        if (current === '') return null;
                        const exists = embeddingDimensionsOptions.some((d) => d === current);
                        if (!exists) {
                            return <MenuItem value={current}>{t('vectorStores.currentOption', 'Current: {{value}}', { value: current })}</MenuItem>;
                        }
                        return null;
                    })()}
                    {embeddingDimensionsOptions.map((d) => (
                        <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                </TextField>
                <FormControlLabel
                    control={
                        <Switch
                            checked={Boolean(hasEmbeddings)}
                            onChange={(e) => setHasEmbeddings(e.target.checked)}
                            disabled={submitting}
                        />
                    }
                    label={t('vectorStores.hasEmbeddings', 'Has Embeddings')}
                />
                <TextField
                    label={t('vectorStores.description', 'Description')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    disabled={submitting}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>{t('vectorStores.cancel', 'Cancel')}</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || submitting}>
                    {mode === 'edit' ? t('vectorStores.update', 'Update') : t('vectorStores.create', 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function VectorStores() {
    const { t } = useTranslation();
    const [vectorStores, setVectorStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [editingVectorStore, setEditingVectorStore] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingVectorStore, setDeletingVectorStore] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const loadVectorStores = useCallback(async () => {
        setLoading(true);
        try {
            const data = await vectorStoreService.getVectorStores();
            setVectorStores(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load vector stores', e);
            setSnackbar({ open: true, message: t('vectorStores.failedToLoad', 'Failed to load vector stores'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadVectorStores();
    }, [loadVectorStores]);

    const sortedVectorStores = useMemo(() => {
        const list = Array.isArray(vectorStores) ? [...vectorStores] : [];
        list.sort((a, b) => {
            const an = normalizeString(a?.name);
            const bn = normalizeString(b?.name);
            if (an && bn) return an.localeCompare(bn);
            return normalizeString(a?.vectorStoreId).localeCompare(normalizeString(b?.vectorStoreId));
        });
        return list;
    }, [vectorStores]);

    const openCreate = () => {
        setDialogMode('create');
        setEditingVectorStore(null);
        setDialogOpen(true);
    };

    const openEdit = (vs) => {
        const id = vs?.vectorStoreId;
        if (!id) return;

        setSaving(true);
        (async () => {
            try {
                const full = await vectorStoreService.getVectorStore(id);
                setDialogMode('edit');
                setEditingVectorStore(full || vs || null);
                setDialogOpen(true);
            } catch (e) {
                console.error('Failed to load vector store', e);
                setSnackbar({ open: true, message: t('vectorStores.failedToLoadDetails', 'Failed to load vector store details'), severity: 'error' });
                setDialogMode('edit');
                setEditingVectorStore(vs || null);
                setDialogOpen(true);
            } finally {
                setSaving(false);
            }
        })();
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
    };

    const handleSubmitDialog = async (payload) => {
        setSaving(true);
        try {
            if (dialogMode === 'edit') {
                const id = editingVectorStore?.vectorStoreId;
                const sanitized = { ...(editingVectorStore || {}), ...payload, vectorStoreId: id };
                // Avoid sending deprecated fields to the backend.
                delete sanitized.llmVendor;
                delete sanitized.llmModel;
                // Backend no longer accepts/returns apiKey; never send it.
                delete sanitized.apiKey;
                await vectorStoreService.updateVectorStore(id, {
                    ...sanitized,
                });
                setSnackbar({ open: true, message: t('vectorStores.updated', 'Vector store updated'), severity: 'success' });
            } else {
                const sanitized = { ...(payload || {}) };
                delete sanitized.llmVendor;
                delete sanitized.llmModel;
                delete sanitized.apiKey;
                await vectorStoreService.createVectorStore(sanitized);
                setSnackbar({ open: true, message: t('vectorStores.created', 'Vector store created'), severity: 'success' });
            }
            setDialogOpen(false);
            await loadVectorStores();
        } catch (e) {
            console.error('Failed to save vector store', e);
            setSnackbar({ open: true, message: t('vectorStores.failedToSave', 'Failed to save vector store'), severity: 'error' });
            throw e;
        } finally {
            setSaving(false);
        }
    };

    const openDeleteDialog = (vs) => {
        if (saving) return;
        if (!vs?.vectorStoreId) return;
        setDeletingVectorStore(vs);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        if (saving) return;
        setDeleteDialogOpen(false);
    };

    const confirmDelete = async () => {
        const id = deletingVectorStore?.vectorStoreId;
        if (!id) return;

        setSaving(true);
        try {
            await vectorStoreService.deleteVectorStore(id);
            setSnackbar({ open: true, message: t('vectorStores.deleted', 'Vector store deleted'), severity: 'success' });
            setDeleteDialogOpen(false);
            setDeletingVectorStore(null);
            await loadVectorStores();
        } catch (e) {
            console.error('Failed to delete vector store', e);
            setSnackbar({ open: true, message: t('vectorStores.failedToDelete', 'Failed to delete vector store'), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const headCellSx = {
        fontWeight: 700,
        fontSize: '0.75rem',
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: '#8a9bb5',
        backgroundColor: '#131926',
        borderBottom: '2px solid rgba(40,123,214,0.35)',
        py: 1.5,
    };

    return (
        <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: '#287bd6', flexShrink: 0 }} />
                <Box sx={{ mr: 'auto' }}>
                    <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: '#1e2030', lineHeight: 1.2 }}>{t('sideBar.vectorStores', 'Vector Stores')}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7a99' }}>{t('vectorStores.records', '{{num}} records', { num: sortedVectorStores.length })}</Typography>
                </Box>
                <Button
                    variant="contained"
                    onClick={openCreate}
                    disabled={loading || saving}
                    sx={{
                        borderRadius: '8px',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #287bd6 0%, #1f6fca 100%)',
                        boxShadow: '0 2px 10px rgba(40,123,214,0.35)',
                        '&:hover': { background: 'linear-gradient(135deg, #3f8fe0 0%, #287bd6 100%)' },
                    }}
                >
                    {t('vectorStores.newVectorStore', 'New Vector Store')}
                </Button>
            </Box>

            {loading ? <Alert severity="info">{t('vectorStores.loading', 'Loading vector stores…')}</Alert> : null}

            {!loading && sortedVectorStores.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed rgba(0,0,0,0.13)', borderRadius: '10px' }}>
                    <Typography variant="body1" sx={{ color: '#6b7a99' }}>{t('vectorStores.noVectorStores', 'No vector stores found.')}</Typography>
                </Paper>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: '1.5px solid rgba(0,0,0,0.13)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headCellSx}>{t('vectorStores.name', 'Name')}</TableCell>
                                <TableCell sx={headCellSx}>{t('vectorStores.colIndex', 'Index')}</TableCell>
                                <TableCell sx={headCellSx}>{t('vectorStores.vendor', 'Vendor')}</TableCell>
                                <TableCell sx={headCellSx}>{t('vectorStores.colEmbedding', 'Embedding')}</TableCell>
                                <TableCell sx={headCellSx}>{t('vectorStores.dimensions', 'Dimensions')}</TableCell>
                                <TableCell sx={headCellSx}>{t('vectorStores.hasEmbeddings', 'Has Embeddings')}</TableCell>
                                <TableCell sx={headCellSx}>{t('vectorStores.colCreated', 'Created')}</TableCell>
                                <TableCell sx={headCellSx}>{t('vectorStores.description', 'Description')}</TableCell>
                                <TableCell align="right" sx={headCellSx}>{t('vectorStores.colActions', 'Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedVectorStores.map((vs) => {
                                const id = vs?.vectorStoreId || '';
                                const embedding = [
                                    normalizeString(vs?.embeddingVendor),
                                    normalizeString(vs?.embeddingModel),
                                ].filter(Boolean).join(' / ');
                                return (
                                    <TableRow
                                        key={id || vs?.name}
                                        sx={{
                                            transition: 'background-color 0.15s ease',
                                            '&:hover td': { backgroundColor: 'rgba(40,123,214,0.18)', transition: 'background-color 0.15s ease' },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}
                                    >
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: '#1e2030' }}>{vs?.name || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{vs?.indexName || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{vs?.vendor || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{embedding || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{Number.isFinite(Number(vs?.dimensions)) ? vs?.dimensions : '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Box sx={{
                                                display: 'inline-block',
                                                px: 1, py: 0.25,
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                backgroundColor: Boolean(vs?.hasEmbeddings ?? vs?.hasEmbeddins)
                                                    ? 'rgba(45,106,79,0.12)' : 'rgba(0,0,0,0.06)',
                                                color: Boolean(vs?.hasEmbeddings ?? vs?.hasEmbeddins)
                                                    ? '#2d6a4f' : '#6b7a99',
                                            }}>
                                                {Boolean(vs?.hasEmbeddings ?? vs?.hasEmbeddins) ? t('vectorStores.yes', 'Yes') : t('vectorStores.no', 'No')}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem', color: '#6b7a99' }}>{formatCreatedAt(vs?.createdAt) || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 320, fontSize: '0.82rem', color: '#5a6275' }}>{vs?.description || ''}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Button size="small" onClick={() => openEdit(vs)} disabled={saving}
                                                sx={{ borderRadius: '6px', fontWeight: 600, color: '#1e2030', '&:hover': { backgroundColor: 'rgba(40,123,214,0.08)', color: '#287bd6' } }}>
                                                {t('vectorStores.edit', 'Edit')}
                                            </Button>
                                            <Button size="small" onClick={() => openDeleteDialog(vs)} disabled={saving}
                                                sx={{ borderRadius: '6px', fontWeight: 600, color: '#287bd6', '&:hover': { backgroundColor: 'rgba(40,123,214,0.08)' } }}>
                                                {t('vectorStores.delete', 'Delete')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
                <DialogTitle>{t('vectorStores.deleteTitle', 'Delete Vector Store')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('vectorStores.deleteConfirmText', 'This will permanently delete {{name}}.', {
                            name: normalizeString(deletingVectorStore?.name) || normalizeString(deletingVectorStore?.vectorStoreId) || t('vectorStores.thisVectorStore', 'this vector store'),
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDeleteDialog} disabled={saving}>{t('vectorStores.cancel', 'Cancel')}</Button>
                    <Button variant="contained" color="error" onClick={confirmDelete} disabled={saving}>
                        {t('vectorStores.delete', 'Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <VectorStoreDialog
                open={dialogOpen}
                mode={dialogMode}
                initialVectorStore={editingVectorStore}
                onClose={closeDialog}
                onSubmit={handleSubmitDialog}
                submitting={saving}
            />

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default VectorStores;
